import { ColumnDef } from '@tanstack/react-table';
import i18n from 'i18next';

import { Tag } from '@/components/generic';
import { TagValue } from '@/components/generic/Tag/tag-config';
import { TributeVideoExportStatus } from '@/enums';
import { formatDate } from '@/helpers/date';
import { DangerIcon } from '@/resources';
import { TableData, TributeVideoExport } from '@/types';

const CLICKABLE_CELL_STYLES =
  'flex cursor-pointer justify-end text-primary-black hover:text-accent-blue hover:underline md:justify-start min-w-0';
const CELL_STYLES = 'flex justify-end text-primary-black md:justify-start';

const mapStatusToTagValue = (status: TributeVideoExportStatus): string => {
  switch (status) {
    case TributeVideoExportStatus.Preparing:
      return TagValue.PREPARING;
    case TributeVideoExportStatus.Pending:
      return TagValue.PENDING;
    case TributeVideoExportStatus.InProgress:
      return TagValue.IN_PROGRESS;
    case TributeVideoExportStatus.RenderingCompleted:
      return TagValue.RENDERING_COMPLETED;
    case TributeVideoExportStatus.Completed:
      return TagValue.COMPLETED;
    case TributeVideoExportStatus.Failed:
      return TagValue.FAILED;
    case TributeVideoExportStatus.Canceled:
      return TagValue.CANCELED;
    default:
      return TagValue.NO_STATUS;
  }
};

export const getTributeVideoExportColumns = (
  onTributeVideoClick?: (tributeVideoId: number, eventId: number) => void,
  onEventClick?: (eventId: number) => void,
  onOrganizationClick?: (organizationId: number) => void
): ColumnDef<TableData>[] => [
  {
    id: 'type',
    header: i18n.t('type', { ns: 'common' }),
    size: 100,
    cell: (props) => {
      const tributeVideoExport = props.row.original as TributeVideoExport;
      return <Tag value={tributeVideoExport.isPreview ? TagValue.PREVIEW : TagValue.FULL_VIDEO} />;
    }
  },
  {
    id: 'tributeVideoName',
    header: i18n.t('tributeVideo', { ns: 'common' }),
    size: 250,
    accessorKey: 'tributeVideoName',
    cell: (props) => {
      const tributeVideoExport = props.row.original as TributeVideoExport;
      const handleClick = () => {
        onTributeVideoClick?.(tributeVideoExport.tributeVideoId, tributeVideoExport.eventId);
      };

      return (
        <div className={`${CLICKABLE_CELL_STYLES} max-w-[250px] font-medium`} onClick={handleClick}>
          <span className='block truncate' style={{ flexShrink: 1 }}>
            {String(props.getValue())}
          </span>
        </div>
      );
    }
  },
  {
    id: 'status',
    header: i18n.t('status', { ns: 'common' }),
    size: 180,
    cell: (props) => {
      const tributeVideoExport = props.row.original as TributeVideoExport;
      const tagValue = mapStatusToTagValue(tributeVideoExport.status);
      const errMsg = tributeVideoExport.errorMessage;

      return (
        <div className='flex w-full items-center'>
          <Tag value={tagValue} />
          {tagValue === TagValue.FAILED && errMsg ? (
            <span className='ml-2 hidden md:inline' title={errMsg}>
              <DangerIcon className='size-5 align-middle' />
            </span>
          ) : null}
        </div>
      );
    }
  },
  {
    id: 'eventName',
    header: i18n.t('event', { ns: 'common' }),
    accessorKey: 'eventName',
    size: 220,
    cell: (props) => {
      const tributeVideoExport = props.row.original as TributeVideoExport;
      const handleClick = () => {
        onEventClick?.(tributeVideoExport.eventId);
      };

      return (
        <div className={`${CLICKABLE_CELL_STYLES} max-w-[220px]`} onClick={handleClick}>
          <span className='block truncate' style={{ flexShrink: 1 }}>
            {String(props.getValue())}
          </span>
        </div>
      );
    }
  },
  {
    id: 'organizationName',
    header: i18n.t('organization', { ns: 'common' }),
    accessorKey: 'organizationName',
    size: 240,
    cell: (props) => {
      const tributeVideoExport = props.row.original as TributeVideoExport;
      const handleClick = () => {
        onOrganizationClick?.(tributeVideoExport.organizationId);
      };

      return (
        <div className={`${CLICKABLE_CELL_STYLES} max-w-[240px]`} onClick={handleClick}>
          <span className='block truncate' style={{ flexShrink: 1 }}>
            {String(props.getValue())}
          </span>
        </div>
      );
    }
  },
  {
    id: 'progress',
    header: i18n.t('progress', { ns: 'common' }),
    size: 100,
    cell: (props) => {
      const tributeVideoExport = props.row.original as TributeVideoExport;

      return <div className={CELL_STYLES}>{tributeVideoExport.progress}%</div>;
    }
  },
  {
    id: 'createdAt',
    header: i18n.t('createdAt', { ns: 'common' }),
    size: 180,
    cell: (props) => {
      const tributeVideoExport = props.row.original as TributeVideoExport;
      return (
        <div className={CELL_STYLES}>{formatDate(new Date(tributeVideoExport.createdAt))}</div>
      );
    }
  },
  {
    id: 'updatedAt',
    header: i18n.t('updatedAt', { ns: 'common' }),
    size: 180,
    cell: (props) => {
      const tributeVideoExport = props.row.original as TributeVideoExport;
      if (!tributeVideoExport.updatedAt) {
        return <div className={CELL_STYLES}>-</div>;
      }
      return (
        <div className={CELL_STYLES}>{formatDate(new Date(tributeVideoExport.updatedAt))}</div>
      );
    }
  },
  {
    id: 'createdBy',
    header: i18n.t('createdBy', { ns: 'common' }),
    accessorKey: 'createdBy',
    size: 220,
    cell: (props) => <div className={CELL_STYLES}>{props.getValue() as string}</div>
  }
];
